import { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { MisdeliveryBolFormValues } from "../../@hooks/useMisdeliveryBolForm";
import { getMisDelivery } from "../../API/getMisDelivery";
import { sendDispositionMailApi } from "../../API/sendDispositionMail";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { normalizeEmptyFields } from "../../helpers";
import { MisDeliveryStatus } from "../../models";
import { env } from "../../constants";
import {
  validateEmails,
  getHazmatEmailContent,
  getNormalEmailContent,
  getEmailSubject,
  downloadFile,
} from "./BOLEmail.utils";
import { EMAIL_TYPES, ERROR_MESSAGES } from "./BOLEmail.constants";

// Types
interface EmailState {
  to: string;
  cc: string;
  subject: string;
  html: string;
  error: string;
}

interface UseBolEmailReturn {
  // State
  loading: boolean;
  bol: MisdeliveryBolFormValues | null;
  pdfFile: File | null;
  emailState: EmailState;
  isHazmatPending: boolean;
  // Actions
  setTo: (value: string) => void;
  setCc: (value: string) => void;
  setHtml: (value: string) => void;
  clearError: () => void;
  handleSend: () => Promise<void>;
  handleDownloadPdf: () => void;
  setPdfFile: (file: File | null) => void;
  setLoading: (loading: boolean) => void;
}

const BOL_NUMERIC_FIELDS: (keyof MisdeliveryBolFormValues)[] = [
  "pickupNumber",
  "pickupPhoneNumber",
  "totalPiecesMisdelivered",
  "weight",
  "proNumber",
  "misdeliveredPalletPro",
  "misdeliveredBolPro",
];

export const useBolEmail = (): UseBolEmailReturn => {
  const { osdNumber } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Core state
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(MisDeliveryStatus.exceptionEntry);
  const [bol, setBol] = useState<MisdeliveryBolFormValues | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  // Email form state
  const [to, setTo] = useState("");
  const [cc, setCc] = useState(env.bolEmailCC || "");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [error, setError] = useState("");

  const isHazmatPending = status === MisDeliveryStatus.hazmatPending;

  // Notification helper
  const showNotification = useCallback(
    (type: NOTIFICATION_TYPE, message: string) => {
      dispatch(PopoutNotification(type, message, Date.now().toString()));
    },
    [dispatch],
  );

  // Navigation helper
  const navigateToEntry = useCallback(() => {
    navigate(`/misdelivery/entry/${encodeURIComponent(osdNumber ?? "")}`);
  }, [navigate, osdNumber]);

  // Fetch exception details
  const fetchExceptionDetails = useCallback(async () => {
    if (!osdNumber) return;

    setLoading(true);
    try {
      const response = await getMisDelivery(osdNumber);
      const { bol: bolData, investigationStatusNumber } = response ?? {};
      setStatus(investigationStatusNumber);
      let normalizedBol = normalizeEmptyFields(bolData, BOL_NUMERIC_FIELDS);

      // If Hazmat, fetch UN/NA details and assign formatted string
      if (
        normalizedBol?.isMisdeliveryHazmat === "Y" &&
        normalizedBol.unNumber
      ) {
        try {
          const { getUnNaNumbersAPI } =
            await import("../../API/getUnNaNumbers");
          const unNumber = normalizedBol.unNumber;
          const unNumberSequence = normalizedBol.unNumberSequence;
          const resp = await getUnNaNumbersAPI({
            search: unNumber,
            page: 1,
            limit: 50,
          });
          const match = resp.data.find(
            (item) => item.sequence === unNumberSequence,
          );
          if (match) {
            // Format: sequence - id, name, class, packingGroup, label
            // if any value is empty then duplicate comma to maintain format

           const values = [
              match.id,
              match.name,
              match.class,
              match.packingGroup,
               match.label,
            ].map(v => v ?? '');

            while (values.length && values[values.length - 1] === '') {
              values.pop();
            }

            normalizedBol.unNumber = values.join(', ');

          }
        } catch (err) {
          console.error("Failed to fetch UN/NA details", err);
          // Proceed without UN/NA details if fetch fails
        }
      }

      setBol(normalizedBol as MisdeliveryBolFormValues | null);
    } catch (err) {
      const message =
        typeof err === "string" ? err : ERROR_MESSAGES.FETCH_ERROR;
      showNotification(NOTIFICATION_TYPE.ERROR, message);
      console.error(ERROR_MESSAGES.FETCH_ERROR, err);
    } finally {
      setLoading(false);
    }
  }, [osdNumber, showNotification]);

  // Update email fields based on status
  const updateEmailFields = useCallback(async () => {
    if (!bol) return;

    const newSubject = getEmailSubject(bol.pickupNumber, isHazmatPending);
    setSubject(newSubject);

    if (isHazmatPending) {
      setTo(env.managerEmail ?? "");
      setCc("");
      setError("");
      const content = await getHazmatEmailContent(osdNumber ?? "");
      setHtml(content);
    } else {
      setTo("");
      setCc(env.bolEmailCC || "");
      const content = await getNormalEmailContent(bol);
      setHtml(content);
    }
  }, [bol, isHazmatPending, osdNumber]);

  // Send email handler
  const handleSend = useCallback(async () => {
    const validation = validateEmails(to, true);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    setLoading(true);
    try {
      const params = {
        to,
        cc: isHazmatPending ? undefined : cc,
        subject,
        html,
        attachmentImageUrls: [],
        emailType: isHazmatPending
          ? EMAIL_TYPES.HAZMAT_PENDING
          : EMAIL_TYPES.BOL,
        files: [],
      };

      const attachments = pdfFile ? [pdfFile] : [];
      await sendDispositionMailApi(osdNumber?.toString(), params, attachments);

      showNotification(NOTIFICATION_TYPE.SUCCESS, ERROR_MESSAGES.SEND_SUCCESS);
      navigateToEntry();
    } catch (err) {
      const message = typeof err === "string" ? err : ERROR_MESSAGES.SEND_ERROR;
      showNotification(NOTIFICATION_TYPE.ERROR, message);
      console.error(ERROR_MESSAGES.SEND_ERROR, err);
    } finally {
      setLoading(false);
    }
  }, [
    to,
    cc,
    subject,
    html,
    pdfFile,
    osdNumber,
    isHazmatPending,
    showNotification,
    navigateToEntry,
  ]);

  // Download PDF handler
  const handleDownloadPdf = useCallback(() => {
    if (pdfFile) {
      downloadFile(pdfFile);
    }
  }, [pdfFile]);

  // Clear error
  const clearError = useCallback(() => setError(""), []);

  // Effects
  useEffect(() => {
    fetchExceptionDetails();
  }, [fetchExceptionDetails]);

  useEffect(() => {
    updateEmailFields();
  }, [updateEmailFields]);

  return {
    loading,
    bol,
    pdfFile,
    emailState: { to, cc, subject, html, error },
    isHazmatPending,
    setTo,
    setCc,
    setHtml,
    clearError,
    handleSend,
    handleDownloadPdf,
    setPdfFile,
    setLoading,
  };
};
